import { Form } from 'antd';
import { useContext, useMemo } from 'react';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import type {
  ExtranetHotelPack,
  ExtranetHotelPackExperiences,
} from 'app/typings/core/extranet/hotel/packs';

import {
  discountToPrice,
  getExperiencePrices,
  priceToDiscount,
} from './experiencePricing';

export const useExperienceDiscount = (
  pack: ExtranetHotelPack,
  packExperience: ExtranetHotelPackExperiences
) => {
  const { getExperience } = useContext(ModernHotelContext);
  const experience = getExperience(packExperience.experienceId);

  const form = Form.useFormInstance();
  const discountFieldName = `experienceDiscount-${pack.id}-${packExperience.experienceId}`;

  const averageRoomPrice = Form.useWatch('averageRoomPrice', form);
  const discount = (Form.useWatch(discountFieldName, form) as number) ?? 0;

  const prices = useMemo(
    () => getExperiencePrices(pack, experience),
    [pack, experience]
  );

  const setDiscountFromPrice =
    (originalPrice: number | undefined) => (discountedPrice: number | null) => {
      if (
        originalPrice === undefined ||
        originalPrice <= 0 ||
        discountedPrice === null
      ) {
        return;
      }

      form.setFieldValue(
        discountFieldName,
        priceToDiscount(originalPrice, discountedPrice)
      );
    };

  const handleDiscountBlur = () => {
    const currentValue = form.getFieldValue(discountFieldName);

    if (currentValue === null || currentValue === undefined) {
      form.setFieldValue(discountFieldName, 0);
    }
  };

  return {
    experience,
    prices,
    discount,
    discountFieldName,
    adultDiscountedPrice: prices
      ? discountToPrice(prices.adultPrice, discount)
      : undefined,
    childDiscountedPrice:
      prices?.childPrice !== undefined
        ? discountToPrice(prices.childPrice, discount)
        : undefined,
    onAdultPriceChange: setDiscountFromPrice(prices?.adultPrice),
    onChildPriceChange: setDiscountFromPrice(prices?.childPrice),
    handleDiscountBlur,
    hasError: !averageRoomPrice,
  };
};
